import { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { RichTextFeedback } from "@/components/admin/exam/RichTextFeedback";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { COLUMNS, PRIORITIES, AREAS, COLOR_LABELS } from "./planningConstants";
import { TaskChecklist } from "./TaskChecklist";
import type { BoardTask } from "@/hooks/useBoardTasks";
import { useAdminMembers } from "@/hooks/useAdminMembers";
import { Diamond, Trash2, ExternalLink, Check, FileText, Tag, Calendar, Paperclip, ListChecks, BookOpen, MessageSquare, Pencil, Lock } from "lucide-react";
import { AusblendenFeld } from "./AusblendenFeld";
import { TaskComments } from "./TaskComments";
import { TaskAttachments, type TaskAttachment } from "./TaskAttachments";
import { BeschreibungAnzeige } from "./BeschreibungAnzeige";
import { QUELLEN, herkunftText, htmlZuKlartext, vorabHinweis } from "./planungAnzeige";
import { transkriptLink } from "./planungHerkunft";
import { DACHZEILE, LEISER_KNOPF } from "./planungGestaltung";
import { ProjektAuswahl } from "./ProjektFeld";

/** Ruhige Fläche statt Verlauf mit Rahmen (Skill musikonzept-design). */
const ABSCHNITT = "rounded-2xl bg-[#FAF9F5] p-4 space-y-3 dark:bg-muted/30";

interface PlanningTaskDialogProps {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  task: BoardTask | null;
  onSave: (data: Record<string, unknown>) => void;
  onDelete?: (id: string) => void;
  isPending: boolean;
  defaultStatus?: string;
  /** Vorbelegtes Projekt bei neuen Aufgaben (z. B. aus dem gesetzten Projektfilter). */
  defaultProjekt?: string | null;
  defaultEntityType?: string;
  defaultEntityId?: string;
  defaultPagePath?: string;
  compact?: boolean;
  /** Wird gerufen, wenn die Aufgabe über „Erledigt“ abgehakt wurde (für die Erfolgs-Animation). */
  onErledigt?: () => void;
}

export function PlanningTaskDialog({
  open,
  onOpenChange,
  task,
  onSave,
  onDelete,
  isPending,
  defaultStatus = "idea",
  defaultProjekt = null,
  compact = false,
  onErledigt,
}: PlanningTaskDialogProps) {
  const isCourseNote = task?.id?.startsWith('cn_');
  const courseTitle = (task as BoardTask & { _courseTitle?: string })?._courseTitle;
  const navigate = useNavigate();
  const { data: currentUser } = useCurrentUser();
  const { data: adminMembers = [] } = useAdminMembers();
  // D27 (Daniel 20.10.2026): Neue Aufgaben sieht zuerst nur, wer hier steht (Daniel und Bianca), dazu wer anlegt und wer
  // sie bekommt. Ausgeblendet werden die übrigen beim Anlegen in der Datenbank (Trigger, Migration 20261020200000).
  const { data: vorab = [] } = useQuery({
    queryKey: ["planung-vorab-sichtbar"],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("planung_vorab_sichtbar_liste");
      if (error) throw error;
      return (data ?? []).filter((v) => v.aktiv).map((v) => v.name);
    },
    staleTime: 30 * 60 * 1000,
  });
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState(defaultStatus);
  const [priority, setPriority] = useState("normal");
  const [area, setArea] = useState("Allgemein");
  const [assignedTo, setAssignedTo] = useState("__none__");
  const [dueDate, setDueDate] = useState("");
  const [startDate, setStartDate] = useState("");
  const [milestone, setMilestone] = useState(false);
  const [colorLabel, setColorLabel] = useState("__none__");
  const [taskAttachments, setTaskAttachments] = useState<TaskAttachment[]>([]);
  const [ausgeblendet, setAusgeblendet] = useState<string[]>([]);
  const [quelle, setQuelle] = useState("manuell");
  const [quelleInfo, setQuelleInfo] = useState("");
  const [quelleLink, setQuelleLink] = useState("");
  const [projekt, setProjekt] = useState<string | null>(null);
  // Bestehende Aufgaben zeigen die Beschreibung erst gelesen (bereinigtes HTML), Bearbeiten auf Knopfdruck
  const [beschreibungBearbeiten, setBeschreibungBearbeiten] = useState(false);
  useEffect(() => {
    setBeschreibungBearbeiten(!task);
    if (task) {
      setTitle(task.title);
      setDescription(task.description || "");
      setStatus(task.status);
      setPriority(task.priority || "normal");
      setArea(task.area || "Allgemein");
      setAssignedTo(task.assigned_to || "__none__");
      setDueDate(task.due_date || "");
      setStartDate(task.start_date || "");
      setMilestone(task.milestone || false);
      setColorLabel(task.color_label || "__none__");
      setTaskAttachments(
        Array.isArray(task.attachments) ? task.attachments as unknown as TaskAttachment[] : []
      );
      setAusgeblendet(task.ausgeblendet_fuer ?? []);
      setQuelle(task.quelle ?? "manuell");
      setQuelleInfo(task.quelle_info ?? "");
      setQuelleLink(task.quelle_link ?? "");
      setProjekt(task.projekt ?? null);
    } else {
      setTitle("");
      setDescription("");
      setStatus(defaultStatus);
      setPriority("normal");
      setArea("Allgemein");
      setAssignedTo(currentUser?.id || "__none__");
      setDueDate("");
      setStartDate("");
      setMilestone(false);
      setColorLabel("__none__");
      setTaskAttachments([]);
      setAusgeblendet([]);
      setQuelle("manuell");
      setQuelleInfo("");
      setQuelleLink("");
      setProjekt(defaultProjekt ?? null);
    }
  }, [task, open, defaultStatus, defaultProjekt, currentUser?.id]);

  // Ausgeblendete Personen können die Aufgabe weder bekommen noch sehen
  const zuweisbar = adminMembers.filter((m) => !ausgeblendet.includes(m.user_id));
  const erstellerId = task ? task.created_by : currentUser?.id ?? null;

  // Track original assigned_to to detect changes
  const originalAssignedTo = useRef<string | null>(null);
  useEffect(() => {
    if (task && open) {
      originalAssignedTo.current = task.assigned_to || null;
    }
  }, [task, open]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;

    const finalAssignedTo = assignedTo === "__none__" ? null : assignedTo;

    // Mail nur, wenn jemand ANDERES neu zugewiesen wird (kein Self-Assign).
    // Board-Aufgaben meldet die Datenbank selbst (Trigger → board_notifications → planung-mails),
    // hier nur noch Kurs-Aufgaben (cn_).
    if (
      isCourseNote &&
      finalAssignedTo &&
      finalAssignedTo !== originalAssignedTo.current &&
      finalAssignedTo !== currentUser?.id
    ) {
      // Fire-and-forget: Mail darf onSave NICHT blockieren/verzögern,
      // aber Fehler dürfen nicht still verschluckt werden.
      supabase.functions
        .invoke("send-task-assignment-email", {
          body: {
            assignedToUserId: finalAssignedTo,
            taskTitle: title.trim(),
            taskId: task?.id,
          },
        })
        .then(({ error }) => {
          if (error) {
            console.error("send-task-assignment-email Fehler:", error);
            toast.warning("Aufgabe gespeichert, aber die Benachrichtigung konnte nicht gesendet werden.");
          }
        })
        .catch((err) => {
          console.error("send-task-assignment-email Exception:", err);
          toast.warning("Aufgabe gespeichert, aber die Benachrichtigung konnte nicht gesendet werden.");
        });
    }


    onSave({
      title: title.trim(),
      description: description || null,
      status,
      priority,
      area,
      assigned_to: finalAssignedTo,
      due_date: dueDate || null,
      start_date: startDate || null,
      milestone,
      color_label: colorLabel === "__none__" ? null : colorLabel,
      attachments: taskAttachments.length > 0 ? JSON.parse(JSON.stringify(taskAttachments)) : [],
      ...(isCourseNote
        ? {}
        : {
            ausgeblendet_fuer: ausgeblendet,
            // Nur erlaubte Werte schicken (CHECK board_tasks_quelle_check); ein leerer Wert aus der Auswahl wird „manuell“.
            quelle: QUELLEN.some((q) => q.wert === quelle) ? quelle : (task?.quelle ?? "manuell"),
            quelle_info: quelleInfo.trim() || null,
            // Nur https; anderes lehnt die Datenbank ab (board_tasks_quelle_link_check) und die Seite sagt es
            quelle_link: quelleLink.trim() || null,
            projekt,
          }),
    });
  };

  /** Build a human-readable label for the linked page path */
  const getPageLabel = (path: string): string => {
    if (path.includes("/kurse/edit/")) return "Zum Kurs / Lektion →";
    if (path.includes("/webinare")) return "Zu Webinare →";
    if (path.includes("/community")) return "Zur Community →";
    return "Zur Seite →";
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto p-0 gap-0">
        {/* Header bar */}
        <DialogHeader className="border-b border-[#E4E9E8] px-6 py-4">
          <DialogTitle className="font-display text-xl font-bold text-[#1D2934] dark:text-foreground">
            {isCourseNote ? "Kurs-Aufgabe bearbeiten" : task ? "Aufgabe bearbeiten" : "Neue Aufgabe"}
          </DialogTitle>
          {task && !isCourseNote && (
            <p className="text-xs text-muted-foreground pt-1">
              Erstellt von{" "}
              <span className="font-medium text-foreground">
                {task.created_by
                  ? adminMembers.find((m) => m.user_id === task.created_by)?.displayName || "Unbekannt"
                  : task.quelle === "claude" ? "Claude" : "Unbekannt"}
              </span>{" "}
              am{" "}
              {new Date(task.created_at).toLocaleString("de-DE", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })}{" "}
              Uhr
              {task.quelle && task.quelle !== "manuell" && (
                <> · {herkunftText(task.quelle, task.quelle_info)}</>
              )}
              {transkriptLink(task.quelle_link) && (
                <>
                  {" · "}
                  <a
                    href={transkriptLink(task.quelle_link)!}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 font-semibold text-[#227277] underline underline-offset-2 hover:text-[#1B5E62]"
                  >
                    <FileText className="h-3 w-3" /> Transkript öffnen
                  </a>
                </>
              )}
            </p>
          )}
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-0 lg:divide-x">
          {/* LEFT: Task details */}
          <div className="p-6 space-y-4 max-h-[78vh] overflow-y-auto">
            {/* Context link */}
            {task?.linked_page_path && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-fit text-xs gap-1.5"
                onClick={() => {
                  onOpenChange(false);
                  navigate(task.linked_page_path!);
                }}
              >
                <ExternalLink className="h-3 w-3" />
                {getPageLabel(task.linked_page_path)}
              </Button>
            )}

            {/* Course context info */}
            {isCourseNote && courseTitle && (
              <p className="flex items-center gap-1.5 text-sm text-[#9A5B00]">
                <BookOpen className="h-4 w-4" /> {courseTitle}
              </p>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
          {/* Titel */}
          <div>
            <Label className={DACHZEILE}>Titel</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Was soll erledigt werden?"
              autoFocus={!task}
              className="mt-1 h-11 rounded-xl border-[#E4E9E8] bg-white text-base font-semibold focus-visible:ring-[#227277] dark:bg-background"
            />
          </div>

          {/* Beschreibung */}
          {!compact && (
            <section className={ABSCHNITT}>
              <div className="flex items-center justify-between gap-2">
                <h4 className={`${DACHZEILE} flex items-center gap-1.5`}>
                  <FileText className="h-3.5 w-3.5" /> Beschreibung
                </h4>
                {!beschreibungBearbeiten && (
                  <button type="button" className={LEISER_KNOPF} onClick={() => setBeschreibungBearbeiten(true)}>
                    <Pencil className="h-3.5 w-3.5" /> Bearbeiten
                  </button>
                )}
              </div>
              {beschreibungBearbeiten ? (
                <RichTextFeedback
                  value={description}
                  onChange={setDescription}
                  placeholder="Details..."
                />
              ) : htmlZuKlartext(description) ? (
                <BeschreibungAnzeige beschreibung={description} />
              ) : (
                <button
                  type="button"
                  className="text-left text-[14px] text-[#4E6274] hover:text-[#227277]"
                  onClick={() => setBeschreibungBearbeiten(true)}
                >
                  Noch keine Beschreibung. Hinzufügen
                </button>
              )}
            </section>
          )}

          {/* Klassifizierung */}
          <section className={ABSCHNITT}>
            <h4 className={`${DACHZEILE} flex items-center gap-1.5`}>
              <Tag className="h-3.5 w-3.5" /> Einordnung
            </h4>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-medium text-muted-foreground">Status</Label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger className="bg-background"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {COLUMNS.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-medium text-muted-foreground">Priorität</Label>
                <Select value={priority} onValueChange={setPriority}>
                  <SelectTrigger className="bg-background"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map((p) => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-medium text-muted-foreground">Bereich</Label>
                <Select value={area} onValueChange={setArea}>
                  <SelectTrigger className="bg-background"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {AREAS.map((a) => (
                      <SelectItem key={a} value={a}>{a}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-medium text-muted-foreground">Zugewiesen an</Label>
                <Select value={assignedTo} onValueChange={setAssignedTo}>
                  <SelectTrigger className="bg-background"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Niemand</SelectItem>
                    {zuweisbar.map((m) => (
                      <SelectItem key={m.user_id} value={m.user_id}>
                        {m.displayName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {!isCourseNote && (
                <div className="col-span-2">
                  <Label className="text-xs font-medium text-muted-foreground">Projekt</Label>
                  <ProjektAuswahl wert={projekt} onWert={setProjekt} className="bg-background" />
                </div>
              )}
            </div>
            {!isCourseNote && (
              <div className="grid grid-cols-[140px_1fr] gap-3">
                <div>
                  <Label className="text-xs font-medium text-muted-foreground">Herkunft</Label>
                  <Select value={quelle} onValueChange={(w) => { if (QUELLEN.some((q) => q.wert === w)) setQuelle(w); }}>
                    <SelectTrigger className="bg-background"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {QUELLEN.filter((q) => q.wert !== "claude" || quelle === "claude").map((q) => (
                        <SelectItem key={q.wert} value={q.wert}>{q.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-medium text-muted-foreground">Zur Herkunft</Label>
                  <Input
                    value={quelleInfo}
                    onChange={(e) => setQuelleInfo(e.target.value)}
                    placeholder={quelle === "meeting" ? "z. B. Meeting Daniel/Bianca 08.10." : "optional"}
                    className="bg-background"
                  />
                </div>
                {(quelle === "meeting" || quelleLink) && (
                  <div className="col-span-2">
                    <Label className="text-xs font-medium text-muted-foreground">Link zum Transkript</Label>
                    <Input
                      type="url"
                      inputMode="url"
                      value={quelleLink}
                      onChange={(e) => setQuelleLink(e.target.value)}
                      placeholder="https://drive.google.com/…"
                      className="bg-background"
                    />
                  </div>
                )}
              </div>
            )}
          </section>

          {/* Termine & Label */}
          <section className={ABSCHNITT}>
            <h4 className={`${DACHZEILE} flex items-center gap-1.5`}>
              <Calendar className="h-3.5 w-3.5" /> Termine & Label
            </h4>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-medium text-muted-foreground">Farblabel</Label>
                <Select value={colorLabel} onValueChange={setColorLabel}>
                  <SelectTrigger className="bg-background"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {COLOR_LABELS.map((cl) => (
                      <SelectItem key={cl.value} value={cl.value}>
                        <span className="flex items-center gap-2">
                          {cl.css && <span className={`w-3 h-3 rounded-full ${cl.css}`} />}
                          {cl.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {!compact && (
                <div>
                  <Label className="text-xs font-medium text-muted-foreground">Startdatum</Label>
                  <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="bg-background" />
                </div>
              )}
              {!compact && (
                <div>
                  <Label className="text-xs font-medium text-muted-foreground">Fällig am</Label>
                  <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="bg-background" />
                </div>
              )}
              <div className="flex items-end">
                <div className="flex items-center gap-2 h-10 px-3 rounded-md bg-background border w-full">
                  <Switch checked={milestone} onCheckedChange={setMilestone} id="milestone" />
                  <Label htmlFor="milestone" className="flex items-center gap-1 cursor-pointer text-sm">
                    <Diamond className="h-3.5 w-3.5 text-[#F5A623]" /> Meilenstein
                  </Label>
                </div>
              </div>
            </div>
          </section>

          {/* Sichtbarkeit: Personen ausblenden (nur Board-Aufgaben, nicht Kurs-Aufgaben) */}
          {!isCourseNote && (
            <section className={ABSCHNITT}>
              <h4 className={`${DACHZEILE} flex items-center gap-1.5`}>
                <Lock className="h-3.5 w-3.5" /> Ausblenden für
              </h4>
              {!task && vorab.length > 0 ? (
                <p className="mb-2 rounded-xl bg-[#E6F4F4] px-3 py-2 text-[13px] leading-[18px] text-[#1D2934]">
                  {vorabHinweis(vorab)}
                </p>
              ) : null}
              {task && ausgeblendet.length > 0 ? (
                <button
                  type="button"
                  className={`${LEISER_KNOPF} mb-1`}
                  onClick={() => setAusgeblendet([])}
                >
                  Für alle freigeben
                </button>
              ) : null}
              <AusblendenFeld
                mitglieder={adminMembers}
                ausgeblendet={ausgeblendet}
                onChange={setAusgeblendet}
                erstellerId={erstellerId}
                ichId={currentUser?.id ?? null}
                zugewiesen={assignedTo === "__none__" ? null : assignedTo}
                onZuweisen={(id) => setAssignedTo(id ?? "__none__")}
              />
            </section>
          )}

          {/* Attachments (photos & videos) */}
          {!compact && (
            <section className={ABSCHNITT}>
              <h4 className={`${DACHZEILE} flex items-center gap-1.5`}>
                <Paperclip className="h-3.5 w-3.5" /> Anhänge
              </h4>
              <TaskAttachments
                attachments={taskAttachments}
                onChange={setTaskAttachments}
              />
            </section>
          )}

          {/* Checklist only for existing tasks */}
          {task && (
            <section className={ABSCHNITT}>
              <h4 className={`${DACHZEILE} flex items-center gap-1.5`}>
                <ListChecks className="h-3.5 w-3.5" /> Checkliste
              </h4>
              <TaskChecklist taskId={task.id} />
            </section>
          )}

          <div className="flex justify-between pt-2">
            {task ? (
              <div className="flex gap-2">
                {onDelete && (
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    onClick={() => {
                      onDelete(task.id);
                      onOpenChange(false);
                    }}
                  >
                    <Trash2 className="h-3.5 w-3.5 mr-1" />
                    Löschen
                  </Button>
                )}
                {status !== 'done' && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="rounded-full border-[#227277] text-[#227277] hover:bg-[#E6F4F4]"
                    onClick={() => {
                      setStatus('done');
                      onErledigt?.();
                      onSave({
                        title: title.trim() || task.title,
                        description: description || null,
                        status: 'done',
                        priority,
                        area,
                        assigned_to: assignedTo === "__none__" ? null : assignedTo,
                        due_date: dueDate || null,
                        start_date: startDate || null,
                        milestone,
                        color_label: colorLabel === "__none__" ? null : colorLabel,
                        attachments: taskAttachments.length > 0 ? JSON.parse(JSON.stringify(taskAttachments)) : [],
                      });
                      onOpenChange(false);
                    }}
                  >
                    <Check className="h-3.5 w-3.5 mr-1" />
                    Erledigt
                  </Button>
                )}
              </div>
            ) : <div />}
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Abbrechen
              </Button>
              <Button type="submit" disabled={isPending || !title.trim()}>
                {task ? "Speichern" : "Erstellen"}
              </Button>
            </div>
          </div>
            </form>
          </div>

          {/* RIGHT: Comments / history sidebar — Kommunikationszentrale */}
          {task && (
            <div className="max-h-[78vh] overflow-y-auto bg-[#F2F0EA] p-6 dark:bg-muted/30">
              <h3 className={`${DACHZEILE} mb-1 flex items-center gap-1.5`}>
                <MessageSquare className="h-3.5 w-3.5" /> Kommunikation & Verlauf
              </h3>
              <p className="mb-4 text-xs text-[#4E6274] dark:text-muted-foreground">
                Mit <kbd className="rounded bg-white px-1.5 py-0.5 font-mono text-[10px] text-[#227277]">@</kbd> erwähnst du jemanden, die Person bekommt eine E-Mail.
              </p>
              <TaskComments
                taskId={task.id}
                taskTitle={task.title}
                ausgeblendet={[...new Set([...(task.ausgeblendet_fuer ?? []), ...ausgeblendet])]}
              />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
